import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TestimonialSection from '@/pages/home/components/TestimonialSection';
import { Link } from 'react-router-dom';
import { categories, siteInfo } from '@/mocks/gossip';

const principles = [
  {
    icon: 'ri-eye-line',
    title: '只整理看得见的事实',
    text: '我们只描述画面里真实存在的信息：光线、走位、服装剪裁与配色逻辑，不做没有依据的猜测与延伸解读。',
  },
  {
    icon: 'ri-image-2-line',
    title: '画面质量优先',
    text: '图集在保留细节的前提下做合理压缩，让你放大也能看清面料纹理、妆面过渡与现场灯光的关系。',
  },
  {
    icon: 'ri-links-line',
    title: '来源清晰可追溯',
    text: '内容来自公开的官方发布、品牌资料与现场直出图片，我们只做整理与解读，尊重每一位创作者的劳动。',
  },
  {
    icon: 'ri-refresh-line',
    title: '及时更正',
    text: '如果内容出现偏差或涉及版权问题，收到反馈后我们会尽快核实并调整，更正记录保持公开透明。',
  },
];

const milestones = [
  { year: '2019', title: '从一组红毯生图开始', text: '最初只是一个整理红毯直出图的小栏目，因为图集干净、信息清楚，慢慢被更多人关注。' },
  { year: '2022', title: '形成四个固定栏目', text: '颜值美照、生图红毯、高光同框、造型解析逐步成型，内容从单纯看图延伸到造型解读。' },
  { year: '2026', title: '专注视觉本身', text: '持续投入画面质量与解读深度，希望每一次打开，都是一次真正舒服的视觉体验。' },
];

export default function AboutPage() {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.youhuochigua.com';

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于诱惑吃瓜',
      description: siteInfo.description,
      inLanguage: 'zh-CN',
      url: `${origin}/about`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteInfo.name,
      url: origin,
      email: siteInfo.email,
      slogan: siteInfo.slogan,
      foundingDate: String(siteInfo.since),
      description: siteInfo.description,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: origin },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: `${origin}/about` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title="关于我们 - 诱惑吃瓜 | 专注颜值与视觉盛宴"
        description="诱惑吃瓜成立于2019年，专注颜值与视觉盛宴，用美照生图、红毯造型与同框高光时刻，记录值得放大的画面。了解我们的定位、理念与内容原则。"
        keywords="关于诱惑吃瓜,品牌介绍,网站定位,内容原则,颜值美照,高光同框"
        type="website"
        jsonLd={structuredData}
      />

      <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20empty%20editorial%20studio%20with%20deep%20crimson%20velvet%20drapes%2C%20warm%20golden%20spotlights%2C%20polished%20marble%20floor%20reflections%2C%20soft%20haze%20and%20artistic%20lighting%2C%20luxury%20magazine%20atmosphere%2C%20no%20people%2C%20high%20detail&width=1600&height=800&seq=gossip-about-hero-01&orientation=landscape"
            alt="深红丝绒帷幕与暖金灯光构成的影棚空间"
            title="关于诱惑吃瓜 - 专注颜值与视觉盛宴"
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/88 via-foreground-950/72 to-foreground-950/55" />
        </div>
        <div className="relative mx-auto max-w-[1240px] px-4 py-16 md:px-8 md:py-24">
          <span className="flex w-fit items-center gap-2 rounded-full border border-accent-400/50 px-4 py-1.5 text-[11px] tracking-[0.2em] text-accent-300">
            <i className="ri-sparkling-2-fill text-sm" aria-hidden="true"></i>
            关于我们
          </span>
          <h1 className="mt-5 max-w-3xl font-heading text-3xl font-black leading-tight tracking-wide text-background-50 md:text-5xl">
            专注颜值与视觉盛宴
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200/85 md:text-base">
            诱惑吃瓜是一个以画面为核心的娱乐视觉内容站点。我们相信，好的照片不需要多余解释，但值得被认真整理、被准确描述。
          </p>
          <dl className="mt-10 grid grid-cols-2 gap-6 border-t border-background-100/20 pt-8 md:grid-cols-4">
            {siteInfo.stats.map((item) => (
              <div key={item.label}>
                <dt className="text-xs tracking-wider text-background-200/70">{item.label}</dt>
                <dd className="mt-1 font-heading text-2xl font-bold text-accent-400 md:text-3xl">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-4 py-16 md:px-8 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="flex items-center gap-2 text-xs font-semibold tracking-[0.24em] text-primary-600">
              <span className="h-3 w-1 rounded-full bg-primary-500" aria-hidden="true" />
              品牌介绍
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              从一组红毯生图，走到今天的视觉内容库
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base leading-loose text-foreground-700">
              {siteInfo.name} 起步于 {siteInfo.since} 年，最初只是把红毯直出图按场次整理在一起。
              我们发现，很多读者想看的并不是热闹的标题，而是画面本身——光线怎么打、礼服怎么走线、
              两个人同框时那道刚好的距离。
            </p>
            <p className="mt-5 text-base leading-loose text-foreground-700">
              于是我们顺着这个方向，把内容拆成颜值美照、生图红毯、高光同框和造型解析四个栏目，
              一边收录高清图集，一边补充可读的解读。到今天，站点累计收录超过一万组图集，
              每天仍在持续更新。
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          {milestones.map((item) => (
            <div key={item.year} className="rounded-lg border border-background-200 bg-background-100 p-6">
              <span className="font-heading text-3xl font-black text-primary-500">{item.year}</span>
              <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-20">
        <div className="mx-auto max-w-[1240px] px-4 md:px-8">
          <span className="flex items-center gap-2 text-xs font-semibold tracking-[0.24em] text-primary-600">
            <span className="h-3 w-1 rounded-full bg-primary-500" aria-hidden="true" />
            网站定位
          </span>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            四个栏目，各自解决一类需求
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {categories.map((category) => (
              <Link
                key={category.slug}
                to={`/category/${category.slug}`}
                className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                  <i className={category.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                  {category.name}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                  {category.intro}
                </p>
                <span className="mt-4 flex items-center gap-1 text-xs font-medium text-primary-600">
                  进入栏目
                  <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-4 py-16 md:px-8 md:py-20">
        <span className="flex items-center gap-2 text-xs font-semibold tracking-[0.24em] text-primary-600">
          <span className="h-3 w-1 rounded-full bg-primary-500" aria-hidden="true" />
          内容原则
        </span>
        <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          我们如何对待每一条内容
        </h2>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          {principles.map((item) => (
            <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent-100 text-xl text-accent-700">
                <i className={item.icon} aria-hidden="true"></i>
              </span>
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <TestimonialSection />

      <section className="mx-auto max-w-[1240px] px-4 pt-16 md:px-8 md:pt-20">
        <div className="rounded-lg bg-foreground-950 px-6 py-12 md:px-12 md:py-14">
          <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <span className="flex items-center gap-2 text-xs font-semibold tracking-[0.24em] text-accent-400">
                <span className="h-3 w-1 rounded-full bg-accent-400" aria-hidden="true" />
                联系方式
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
                内容合作与问题反馈
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-200/80 md:text-base">
                无论是图集授权、内容更正，还是你希望我们整理某个场次，都可以通过下面的方式联系到我们。
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <a
                href={`mailto:${siteInfo.email}`}
                rel="nofollow"
                className="flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-mail-line text-lg" aria-hidden="true"></i>
                {siteInfo.email}
              </a>
              <Link
                to="/contact"
                className="flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-6 text-sm font-semibold text-background-50 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                在线留言
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}